import { RouteStop } from '../types';
import { RouteStop as DatabaseRouteStop } from '@/types/technicianRoutes';

/**
 * Calculate distance between two points using the Haversine formula (in miles)
 */
export const calculateDistance = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number => {
  const R = 3959; // Earth's radius in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Optimize waypoint order using nearest-neighbor algorithm.
 * Starts from base location, then always visits the nearest unvisited stop.
 */
export const optimizeWaypointOrder = <
  T extends { dealer: { latitude: number; longitude: number } }
>(
    baseLocation: { lat: number; lng: number },
    stops: T[]
  ): T[] => {
  if (stops.length <= 1) {
    return stops;
  }

  const optimized: T[] = [];
  const remaining = [...stops];
  let currentLat = baseLocation.lat;
  let currentLng = baseLocation.lng;

  while (remaining.length > 0) {
    let nearestIndex = 0;
    let nearestDistance = Infinity;

    for (let i = 0; i < remaining.length; i++) {
      const distance = calculateDistance(
        currentLat,
        currentLng,
        remaining[i].dealer.latitude,
        remaining[i].dealer.longitude
      );
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = i;
      }
    }

    const nextStop = remaining.splice(nearestIndex, 1)[0];
    optimized.push(nextStop);
    currentLat = nextStop.dealer.latitude;
    currentLng = nextStop.dealer.longitude;
  }

  return optimized;
};

/**
 * Optimize route stops using nearest-neighbor algorithm and return
 * stops with updated stop_order values (1-based).
 */
export const optimizeRouteStops = (
  stops: RouteStop[],
  baseLocation: { lat: number; lng: number }
): RouteStop[] => {
  const optimized = optimizeWaypointOrder(baseLocation, stops);
  return optimized.map((stop, index) => ({
    ...stop,
    stop_order: index + 1
  }));
};

/**
 * Optimize database RouteStop array using nearest-neighbor algorithm.
 * Works with RouteStop from technicianRoutes.ts which has lat/lng directly.
 * Updates stop_order and recalculates distance_from_previous.
 */
export const optimizeRouteStopsFromData = (
  stops: DatabaseRouteStop[],
  baseLocation: { lat: number; lng: number }
): DatabaseRouteStop[] => {
  if (stops.length === 0) return stops;

  const optimized: DatabaseRouteStop[] = [];
  const remaining = [...stops];
  let currentLat = baseLocation.lat;
  let currentLng = baseLocation.lng;

  while (remaining.length > 0) {
    let nearestIndex = 0;
    let nearestDistance = Infinity;

    for (let i = 0; i < remaining.length; i++) {
      const dist = calculateDistance(currentLat, currentLng, remaining[i].lat, remaining[i].lng);
      if (dist < nearestDistance) {
        nearestDistance = dist;
        nearestIndex = i;
      }
    }

    const nextStop = remaining.splice(nearestIndex, 1)[0];
    optimized.push({ ...nextStop, distance_from_previous_miles: nearestDistance });
    currentLat = nextStop.lat;
    currentLng = nextStop.lng;
  }

  // Update stop_order (1-based) and estimate duration (2 min/mile)
  return optimized.map((stop, index) => ({
    ...stop,
    stop_order: index + 1,
    duration_from_previous_minutes: Math.round(stop.distance_from_previous_miles * 2)
  }));
};

/**
 * Address type classification based on Nominatim result
 */
export type AddressType = 'street_address' | 'street' | 'city' | 'region' | 'other';

/**
 * Geocoded result with metadata for validation
 */
export interface GeocodedResult {
  lat: number;
  lng: number;
  displayName: string;
  businessName?: string; // POI/business name from Nominatim (e.g., "AutoZone", "Jiffy Lube")
  type: AddressType;
  importance: number; // 0-1 from Nominatim (higher = more specific/important)
  addressComponents: {
    houseNumber?: string;
    road?: string;
    city?: string;
    state?: string;
    postcode?: string;
  };
  warning?: string;
}

/**
 * Address input validation result
 */
export interface AddressValidation {
  valid: boolean;
  error?: string;
  warning?: string;
}

/**
 * Validate address input before geocoding.
 * Returns hard errors for invalid inputs, soft warnings for potentially problematic inputs.
 */
export const validateAddressInput = (input: string): AddressValidation => {
  const trimmed = input.trim();

  if (trimmed.length < 10) {
    return {
      valid: false,
      error: 'Please enter a more complete address (at least 10 characters)'
    };
  }

  // Check for obvious non-address patterns (only digits and spaces)
  if (/^[\d\s]+$/.test(trimmed)) {
    return {
      valid: false,
      error: 'Please include a street name, not just numbers'
    };
  }

  // Encourage specific addresses with multiple words
  if (!trimmed.includes(' ')) {
    return {
      valid: false,
      error: 'Please enter a full address with street and city'
    };
  }

  // PO Box addresses can't be geocoded to specific locations
  if (/\b(p\.?\s*o\.?\s*box|post\s*office\s*box)\b/i.test(trimmed)) {
    return {
      valid: false,
      error: 'PO Box addresses cannot be used for routing. Please enter a street address.'
    };
  }

  // Soft warning: no street number detected (might return vague results)
  const hasStreetNumber = /^\d+\s/.test(trimmed) || /\s\d+\s/.test(trimmed);
  if (!hasStreetNumber) {
    return {
      valid: true,
      warning: 'Tip: Include a street number for more precise results'
    };
  }

  return { valid: true };
};

// Address type classification sets for reduced complexity
const STREET_ADDRESS_TYPES = new Set(['house', 'building']);
const STREET_ADDRESS_ADDRESS_TYPES = new Set(['place']);
const STREET_TYPES = new Set(['road', 'street', 'residential']);
const STREET_ADDRESS_TYPES_2 = new Set(['road']);
const CITY_TYPES = new Set(['city', 'town', 'village', 'hamlet']);
const CITY_ADDRESS_TYPES = new Set(['city']);
const REGION_TYPES = new Set(['state', 'county', 'administrative']);
const REGION_ADDRESS_TYPES = new Set(['state', 'county']);

/**
 * Classify address type from Nominatim result
 */
const classifyAddressType = (result: NominatimResult): AddressType => {
  const type = result.type?.toLowerCase() || '';
  const addressType = result.addresstype?.toLowerCase() || '';

  // Most specific - has house number
  if (STREET_ADDRESS_TYPES.has(type) || STREET_ADDRESS_ADDRESS_TYPES.has(addressType)) {
    return 'street_address';
  }
  if (result.address?.house_number) {
    return 'street_address';
  }

  // Street but no house number
  if (STREET_TYPES.has(type) || STREET_ADDRESS_TYPES_2.has(addressType)) {
    return 'street';
  }

  // City-level
  if (CITY_TYPES.has(type) || CITY_ADDRESS_TYPES.has(addressType)) {
    return 'city';
  }

  // Region-level (too broad)
  if (REGION_TYPES.has(type) || REGION_ADDRESS_TYPES.has(addressType)) {
    return 'region';
  }

  return 'other';
};

// Warning messages for address types
const ADDRESS_WARNINGS: Record<AddressType, string | undefined> = {
  city: 'This is a city. Enter a street address for more accurate routing.',
  region: 'This is a region. Enter a specific street address.',
  street: 'No house number found. Location may be approximate.',
  street_address: undefined,
  other: undefined
};

/**
 * Get warning message based on address type
 */
const getAddressWarning = (type: AddressType): string | undefined => ADDRESS_WARNINGS[type];

/**
 * Nominatim API response structure
 */
interface NominatimResult {
  lat: string;
  lon: string;
  display_name: string;
  name?: string; // POI/business name (e.g., "AutoZone", "Jiffy Lube", "Manheim Auto Auction")
  type?: string;
  class?: string; // Nominatim class (e.g., "amenity", "shop", "building")
  addresstype?: string;
  importance?: number;
  address?: {
    house_number?: string;
    road?: string;
    city?: string;
    town?: string;
    village?: string;
    state?: string;
    postcode?: string;
    amenity?: string; // Business name sometimes here too
    shop?: string;
  };
}

/**
 * Deduplicate geocoded results that are essentially the same location.
 * Results within 0.05 miles (~80 meters) are considered duplicates.
 * Keeps the result with higher importance score.
 */
const deduplicateResults = (results: GeocodedResult[]): GeocodedResult[] => {
  const DUPLICATE_THRESHOLD_MILES = 0.05; // ~80 meters
  const deduplicated: GeocodedResult[] = [];

  for (const result of results) {
    const isDuplicate = deduplicated.some(existing => {
      const distance = calculateDistance(existing.lat, existing.lng, result.lat, result.lng);
      return distance < DUPLICATE_THRESHOLD_MILES;
    });

    if (!isDuplicate) {
      deduplicated.push(result);
    }
  }

  return deduplicated;
};

/**
 * Strip suite/apartment/unit numbers from an address.
 * Nominatim doesn't handle these well, so we remove them before geocoding.
 * Handles: suite, ste, apt, apartment, unit, floor, fl, building, bldg, room, rm, lot, space, #
 */
const stripUnitNumber = (address: string): string => {
  return address
    .replace(/\b(suite|ste)\.?\s*#?\s*\d+[-/]?[a-z]?\b/gi, '')
    .replace(/\b(apt|apartment)\.?\s*#?\s*\d+[-/]?[a-z]?\b/gi, '')
    .replace(/\bunit\.?\s*#?\s*\d+[-/]?[a-z]?\b/gi, '')
    .replace(/\b(floor|fl)\.?\s*\d{1,3}[a-z]?\b/gi, '')
    .replace(/\b(building|bldg)\.?\s*[\da-z]+\b/gi, '')
    .replace(/\b(room|rm)\.?\s*\d{1,4}[a-z]?\b/gi, '')
    .replace(/\blot\.?\s*\d{1,4}[a-z]?\b/gi, '')
    .replace(/\bspace\.?\s*\d{1,3}[a-z]?\b/gi, '')
    .replace(/#\s*\d+[-/]?[a-z]?\b/gi, '')
    .replace(/,\s*,+/g, ',')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s,/g, ',')
    .replace(/,\s*$/g, '')
    .trim();
};

/**
 * Geocode an address string to lat/lng coordinates using Nominatim (OpenStreetMap).
 * Returns multiple results with metadata for user selection.
 * Deduplicates results that are essentially the same location.
 * Free, no API key required. Rate limit: 1 request/second.
 */
export const geocodeAddressMultiple = async (
  address: string,
  limit: number = 3
): Promise<GeocodedResult[]> => {
  // Request more results than needed to account for duplicates
  const requestLimit = Math.min(limit * 2, 6);
  const baseUrl = 'https://nominatim.openstreetmap.org/search';
  // Strip suite/apt/unit numbers as Nominatim doesn't handle them well
  const cleanedAddress = stripUnitNumber(address);
  const query = encodeURIComponent(cleanedAddress);
  const params = `format=json&q=${query}&limit=${requestLimit}&countrycodes=us&addressdetails=1`;
  const url = `${baseUrl}?${params}`;

  const response = await fetch(url, {
    headers: { 'User-Agent': 'Vehix.ai-RouteOptimizer/1.0' }
  });
  const results: NominatimResult[] = await response.json();

  const mappedResults = results.map(result => {
    const type = classifyAddressType(result);
    // Extract business name from various Nominatim fields
    // Priority: name > address.amenity > address.shop
    const businessName =
      result.name || result.address?.amenity || result.address?.shop || undefined;
    return {
      lat: parseFloat(result.lat),
      lng: parseFloat(result.lon),
      displayName: result.display_name,
      type,
      importance: result.importance ?? 0,
      addressComponents: {
        houseNumber: result.address?.house_number,
        road: result.address?.road,
        city: result.address?.city || result.address?.town || result.address?.village,
        state: result.address?.state,
        postcode: result.address?.postcode
      },
      warning: getAddressWarning(type),
      ...(businessName && { businessName })
    };
  });

  // Deduplicate and return up to the requested limit
  return deduplicateResults(mappedResults).slice(0, limit);
};

/**
 * Geocode an address string to lat/lng coordinates using Nominatim (OpenStreetMap).
 * Returns the first result only (legacy compatibility).
 * Free, no API key required. Rate limit: 1 request/second.
 * @deprecated Use geocodeWithMapbox from mapboxGeocoding.ts instead
 */
export const geocodeAddress = async (
  address: string
): Promise<{ lat: number; lng: number; displayName: string } | null> => {
  const results = await geocodeAddressMultiple(address, 1);
  if (results.length > 0) {
    return {
      lat: results[0].lat,
      lng: results[0].lng,
      displayName: results[0].displayName
    };
  }
  return null;
};

// =============================================================================
// Location Proximity Matching
// =============================================================================

/**
 * Threshold for considering two locations as duplicates (~80 meters)
 */
export const DUPLICATE_LOCATION_THRESHOLD_MILES = 0.05;

/**
 * Stop data structure for proximity matching
 */
export interface StopForProximityMatch {
  lat: number;
  lng: number;
  dealer_name: string;
  dealer_id?: string;
  stop_type?: 'recall' | 'manual';
}

/**
 * Result of a proximity match search
 */
export interface ProximityMatchResult {
  match: StopForProximityMatch;
  distanceMiles: number;
}

/**
 * Find an existing stop within proximity threshold of the given coordinates.
 *
 * Used to detect duplicate locations when adding manual stops or recalls.
 * Returns the closest match if one exists within the threshold.
 *
 * @param lat - Latitude of the new stop location
 * @param lng - Longitude of the new stop location
 * @param existingStops - Array of existing stops to check against
 * @param thresholdMiles - Distance threshold in miles (default: 0.05 miles / ~80 meters)
 * @returns The matching stop and distance, or null if no match found
 */
export function findMatchingStopByProximity(
  lat: number,
  lng: number,
  existingStops: StopForProximityMatch[],
  thresholdMiles: number = DUPLICATE_LOCATION_THRESHOLD_MILES
): ProximityMatchResult | null {
  if (!existingStops || existingStops.length === 0) {
    return null;
  }

  let closestMatch: ProximityMatchResult | null = null;

  for (const stop of existingStops) {
    // Skip stops with invalid coordinates
    if (!stop.lat || !stop.lng || (stop.lat === 0 && stop.lng === 0)) {
      continue;
    }

    const distance = calculateDistance(lat, lng, stop.lat, stop.lng);

    if (distance <= thresholdMiles) {
      // Keep track of the closest match
      if (!closestMatch || distance < closestMatch.distanceMiles) {
        closestMatch = {
          match: stop,
          distanceMiles: distance
        };
      }
    }
  }

  return closestMatch;
}

/**
 * Convert distance in miles to a human-readable string.
 */
export function formatProximityDistance(miles: number): string {
  if (miles < 0.01) {
    return 'same location';
  }
  const feet = Math.round(miles * 5280);
  if (feet < 500) {
    return `${feet} ft away`;
  }
  return `${(miles * 1000).toFixed(0)}m away`;
}
